<template>
  <div class="run-canvas position-relative w-100 h-100">
    <VueFlow
      :id="instanceId"
      :key="`runcanvas-${runId}-${layout.flowDirection.value}`"
      :nodes="canvasNodes.enhancedNodes.value"
      :edges="canvasNodes.currentEdges.value"
      :node-types="nodeTypes"
      :edge-types="edgeTypes"
      :nodes-draggable="true"
      :nodes-connectable="false"
      :elements-selectable="true"
      :zoom-on-scroll="false"
      :zoom-on-pinch="true"
      :zoom-on-double-click="true"
      @node-click="(event) => handleNodeClick(event.node.id, event.node.data)"
      @node-drag-stop="layout.onNodeDragStop"
      @pane-ready="layout.restoreViewport"
    >
      <Background pattern-color="var(--flow-bg-pattern-color)" :gap="20" :size="1.9" />
    </VueFlow>

    <!-- Floating control cluster, top-right — same position/style as the builder's.
         An optional #controls-top slot sits above the canvas controls (with a
         divider), so a host surface can mount its view-mode toggle there and get a
         single unified cluster identical to the builder. No Run — read-only. -->
    <div class="run-canvas-controls position-absolute d-flex flex-column gap-1">
      <template v-if="$slots['controls-top']">
        <slot name="controls-top" />
        <span class="border-bottom my-1"></span>
      </template>
      <CanvasControls
        :flow-direction="layout.flowDirection.value"
        @zoom-in="() => layout.zoomIn()"
        @zoom-out="() => layout.zoomOut()"
        @fit-view="() => layout.fitView()"
        @apply-hierarchical-layout="layout.applyLayout"
        @toggle-flow-direction="layout.toggleFlowDirection"
      />
    </div>

    <!-- Node-click drill-in: the same transaction explorer the builder history
         canvas opens. -->
    <RunServiceDetailsPanel
      :is-visible="detailsVisible"
      :node-data="selectedNodeData"
      :node-id="selectedServiceId"
      :run-id="runId"
      @close="closeDetails"
    />
  </div>
</template>

<script lang="ts" setup>
import { ref, toRef, markRaw } from "vue";
import { VueFlow } from "@vue-flow/core";
import { Background } from "@vue-flow/background";
import ServiceNode from "@/views/builder/components/ServiceNode.vue";
import AnimatedEdge from "@/views/builder/components/AnimatedEdge.vue";
import RunServiceDetailsPanel from "@/views/builder/components/RunServiceDetailsPanel.vue";
import CanvasControls from "@/views/runhistory/CanvasControls.vue";
import { useRunCanvasLayout } from "@/composables/useRunCanvasLayout";
import { useRunCanvasNodes } from "@/composables/useRunCanvasNodes";
import type { RunCanvasNode, RunCanvasEdge, ServiceNodeData, ServiceHistory } from "@/types";

// Monotonic per-mount counter → a unique VueFlow store id per RunCanvas instance,
// so two canvases on one page (or a builder canvas nested in the editor's provider)
// never share a store. Not Math.random (banned in some run contexts) — a counter is
// deterministic and sufficient for uniqueness within a page session.
let runCanvasSeq = 0;
function nextRunCanvasId(): number {
  return ++runCanvasSeq;
}

const props = defineProps<{
  // Laid-out history nodes (positions assigned) and styled history edges — the
  // same `historyNodes` / `historyEdges` both surfaces already compute.
  nodes: RunCanvasNode[];
  edges: RunCanvasEdge[];
  // The run on screen. runId identifies the VueFlow instance (keyed) and scopes
  // transaction reads.
  runId: string;
}>();

const nodeTypes: any = { service: markRaw(ServiceNode) };
const edgeTypes: any = { animated: markRaw(AnimatedEdge) };

// Own VueFlow store id, unique per RunCanvas instance. Without it, useVueFlow()
// injects the nearest provided store — and in the builder RunCanvas mounts inside
// the editor's VueFlow provider, so the two canvases would share one store and
// fight over viewport / fitView / nodes. Stable across remounts (flow-direction
// keying) so the same isolated store is reused.
const instanceId = `run-canvas-${nextRunCanvasId()}`;

// Layout owns a local, re-layoutable copy of the source nodes (for LR / fit /
// drag) and reconciles live frames against it without jumping finished services.
// runId lets it tell "a live frame of this run" from "a different run selected" — two runs of one
// workflow share service ids, so without it the second would inherit the first's positions.
const layout = useRunCanvasLayout(toRef(props, "nodes"), toRef(props, "edges"), instanceId, toRef(props, "runId"));

// Nodes enhanced with read-only canvas metadata (leaf detection, mode, direction).
const canvasNodes = useRunCanvasNodes(
  layout.canvasNodes,
  toRef(props, "edges"),
  layout.flowDirection
);

// --- Node-click transaction drill-in --------------------------------------
const detailsVisible = ref(false);
const selectedServiceId = ref<string | null>(null);
const selectedNodeData = ref<{
  label: string;
  serviceType?: string;
  status?: string;
  runData?: ServiceHistory;
} | null>(null);

function handleNodeClick(nodeId: string, nodeData: ServiceNodeData) {
  selectedServiceId.value = nodeId;
  selectedNodeData.value = {
    label: nodeData.label ?? "Service",
    serviceType: nodeData.serviceType,
    status: nodeData.run?.status,
    // The explorer reads runData?.total for its transaction count header.
    runData: nodeData.run,
  };
  detailsVisible.value = true;
}

function closeDetails() {
  detailsVisible.value = false;
  selectedServiceId.value = null;
  selectedNodeData.value = null;
}

// Deep-link support: a parent that lands on a serviceid can open the drill-in
// directly (mirrors the stacked view's expandNode).
function openService(serviceId: string) {
  const node = props.nodes.find((n) => n.id === serviceId);
  if (node?.data) handleNodeClick(serviceId, node.data);
}

defineExpose({ openService });
</script>
